import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowRight,
    Handshake,
    Truck,
    Container,
    Forklift,
    Weight,
    Route,
    AlertTriangle,
    Check,
} from "lucide-react";
import SEO from "../components/SEO";
import Marquee from "../components/Marquee";
import ServiceCard from "../components/ServiceCard";
import CTASection from "../components/CTASection";
import { Reveal, MaskedLine, Eyebrow, EASE } from "../components/Reveal";
import { IMAGES, CAPACITY_STATS, VOLUME_STATS, SERVICES, PROBLEMS } from "../data/site";

const WHY_POINTS = [
    {
        icon: Handshake,
        title: "Family-Owned Since 2002",
        copy: "Long-term relationships and hands-on service backed by more than two decades in logistics.",
    },
    {
        icon: Truck,
        title: "Dedicated Fleet",
        copy: "Pacific operates its own fleet rather than relying solely on brokered transportation.",
    },
    {
        icon: Container,
        title: "Built for Volume",
        copy: "Infrastructure and capacity designed for high-volume container operations.",
    },
    {
        icon: Forklift,
        title: "Equipment Capacity",
        copy: "100+ trucks and 500+ chassis provide substantial operational flexibility.",
    },
    {
        icon: Weight,
        title: "Problem Freight",
        copy: "Experience handling overweight, OOG and difficult freight requirements.",
    },
    {
        icon: Route,
        title: "End-to-End Support",
        copy: "Drayage, transloading, warehousing and transportation under one logistics relationship.",
    },
];

function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

    return (
        <section
            ref={ref}
            data-testid="home-hero"
            className="relative flex min-h-[88svh] items-end overflow-hidden bg-ink"
        >
            <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0" aria-hidden="true">
                <img
                    src={IMAGES.hero}
                    alt=""
                    className="h-full w-full object-cover"
                    fetchpriority="high"
                />
            </motion.div>
            <div
                className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30"
                aria-hidden="true"
            />
            <div
                className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-transparent"
                aria-hidden="true"
            />
            <div className="container-px relative w-full pb-16 pt-40 sm:pb-20 lg:pb-24">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                    className="eyebrow-label"
                >
                    <span className="inline-block h-px w-8 bg-gold" aria-hidden="true" />
                    Family-Owned Since 2002
                </motion.p>
                <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-[68px] font-bold tracking-tight leading-[1.15] sm:leading-[1.1] lg:leading-[1.05] text-white max-w-4xl">
                    <MaskedLine delay={0.25}>Southern California&rsquo;s</MaskedLine>
                    <MaskedLine delay={0.37}>Trusted Logistics</MaskedLine>
                    <MaskedLine delay={0.49}>
                        Partner<span className="text-gold">.</span>
                    </MaskedLine>
                </h1>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
                    className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-gray-300"
                >
                    Port drayage, warehousing, transloading and transportation solutions
                    designed to keep your supply chain moving.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
                    className="mt-9 flex flex-col gap-4 sm:flex-row"
                >
                    <Link
                        to="/contact"
                        data-testid="hero-quote-button"
                        className="group inline-flex items-center justify-center gap-2 bg-gold px-8 py-4 font-heading text-sm font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-200 hover:bg-gold-light"
                    >
                        Request a Quote
                        <ArrowRight
                            size={16}
                            className="transition-transform duration-200 group-hover:translate-x-1"
                            aria-hidden="true"
                        />
                    </Link>
                    <Link
                        to="/solutions"
                        data-testid="hero-solutions-button"
                        className="inline-flex items-center justify-center gap-2 border border-white/30 px-8 py-4 font-heading text-sm font-semibold uppercase tracking-[0.14em] text-white backdrop-blur-sm transition-colors duration-200 hover:border-gold hover:text-gold"
                    >
                        Explore Solutions
                    </Link>
                </motion.div>
            </div>
        </section>
    );
}

function StatsBar() {
    return (
        <section
            data-testid="capacity-bar"
            aria-label="Operational capacity"
            className="relative border-y border-white/10 bg-ink"
        >
            <div className="absolute inset-x-0 top-0 h-[3px] bg-gold" aria-hidden="true" />
            <div className="container-px grid grid-cols-2 divide-x divide-white/10 lg:grid-cols-4">
                {CAPACITY_STATS.map((stat, i) => (
                    <Reveal
                        key={stat.label}
                        delay={i * 0.08}
                        className={`px-4 py-10 text-center sm:px-6 lg:py-14 ${
                            i >= 2 ? "border-t border-white/10 lg:border-t-0" : ""
                        }`}
                    >
                        <p
                            className={`font-heading font-extrabold tracking-tight text-gold ${
                                stat.value.length > 5
                                    ? "text-2xl sm:text-3xl lg:text-4xl"
                                    : "text-3xl sm:text-4xl lg:text-5xl"
                            }`}
                        >
                            {stat.value}
                        </p>
                        <p className="mt-2 text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-gray-400">
                            {stat.label}
                        </p>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

function WhyGrid() {
    return (
        <section className="bg-white py-16 sm:py-20 lg:py-28" data-testid="why-pacific-section">
            <div className="container-px">
                <Reveal className="max-w-3xl">
                    <Eyebrow>Why Pacific Expressway</Eyebrow>
                    <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink">
                        Why Businesses Choose Pacific Expressway
                    </h2>
                </Reveal>
                <div className="mt-12 grid gap-px bg-gray-200 sm:grid-cols-2 lg:mt-16 lg:grid-cols-3">
                    {WHY_POINTS.map((point, i) => (
                        <Reveal key={point.title} delay={(i % 3) * 0.08} className="bg-white">
                            <div className="group h-full p-7 sm:p-9 transition-colors duration-300 hover:bg-mist">
                                <point.icon
                                    size={26}
                                    className="text-gold"
                                    aria-hidden="true"
                                    strokeWidth={1.75}
                                />
                                <h3 className="mt-5 font-heading text-lg sm:text-xl font-semibold uppercase tracking-[0.08em] text-ink">
                                    {point.title}
                                </h3>
                                <p className="mt-3 text-[15px] leading-relaxed text-gray-600">
                                    {point.copy}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function SolutionsPreview() {
    return (
        <section className="bg-mist py-16 sm:py-20 lg:py-28" data-testid="solutions-preview">
            <div className="container-px">
                <Reveal className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                    <div className="max-w-2xl">
                        <Eyebrow>Our Solutions</Eyebrow>
                        <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink">
                            One Partner. Complete Logistics Support.
                        </h2>
                    </div>
                    <p className="max-w-md text-[15px] leading-relaxed text-gray-600">
                        From the port complex to the warehouse floor and the final
                        destination — one accountable team, one dedicated fleet.
                    </p>
                </Reveal>
                <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:mt-16 lg:grid-cols-3 lg:gap-8">
                    {SERVICES.map((service, i) => (
                        <Reveal key={service.id} delay={(i % 3) * 0.08}>
                            <ServiceCard service={service} index={i} />
                        </Reveal>
                    ))}
                </div>
                <Reveal className="mt-12 text-center">
                    <Link
                        to="/solutions"
                        data-testid="view-all-solutions-button"
                        className="group inline-flex items-center gap-2 border-2 border-ink px-8 py-4 font-heading text-sm font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-200 hover:bg-ink hover:text-white"
                    >
                        View All Solutions
                        <ArrowRight
                            size={16}
                            className="transition-transform duration-200 group-hover:translate-x-1"
                            aria-hidden="true"
                        />
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}

function ProblemSolution() {
    return (
        <section
            className="relative overflow-hidden bg-charcoal py-16 sm:py-20 lg:py-28 grain"
            data-testid="problem-solution-section"
        >
            <div
                className="absolute left-0 top-0 h-full w-1 bg-gold"
                aria-hidden="true"
            />
            <div className="container-px grid gap-12 lg:grid-cols-2 lg:gap-16">
                <Reveal>
                    <Eyebrow>The Reality of the Port</Eyebrow>
                    <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
                        Freight Problems Don&rsquo;t Wait.
                    </h2>
                    <p className="mt-6 text-base sm:text-lg leading-relaxed text-gray-400">
                        Neither do we. Pacific combines dedicated fleet capacity, dual
                        yards, heavy equipment and two decades of port experience to keep
                        freight moving when conditions work against you.
                    </p>
                    <Link
                        to="/contact"
                        data-testid="problem-quote-button"
                        className="group mt-8 inline-flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-[0.16em] text-gold"
                    >
                        Talk to Dispatch
                        <ArrowRight
                            size={16}
                            className="transition-transform duration-200 group-hover:translate-x-1"
                            aria-hidden="true"
                        />
                    </Link>
                </Reveal>
                <ul className="grid gap-3 sm:grid-cols-2 lg:gap-4">
                    {PROBLEMS.map((problem, i) => (
                        <Reveal key={problem} delay={i * 0.06}>
                            <li className="flex h-full items-start gap-3 border border-white/10 bg-ink/40 p-5">
                                <AlertTriangle
                                    size={18}
                                    className="mt-0.5 shrink-0 text-gold"
                                    aria-hidden="true"
                                />
                                <span className="text-[15px] font-medium leading-snug text-gray-200">
                                    {problem}
                                </span>
                            </li>
                        </Reveal>
                    ))}
                </ul>
            </div>
        </section>
    );
}

function BuiltForVolume() {
    return (
        <section className="bg-white py-16 sm:py-20 lg:py-28" data-testid="built-for-volume">
            <div className="container-px">
                <Reveal className="max-w-3xl">
                    <Eyebrow>Operational Capacity</Eyebrow>
                    <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink">
                        Built for Volume.
                    </h2>
                    <p className="mt-6 text-base sm:text-lg leading-relaxed text-gray-600">
                        Trucks, chassis, yards and heavy equipment — the physical
                        infrastructure behind every commitment we make.
                    </p>
                </Reveal>
                <div className="mt-12 grid grid-cols-2 gap-4 lg:mt-16 lg:grid-cols-4 lg:gap-5">
                    <Reveal className="col-span-2 row-span-2">
                        <div className="group relative h-full min-h-[280px] overflow-hidden lg:min-h-[420px]">
                            <img
                                src={IMAGES.portCrane}
                                alt="Container cranes working vessels at the Port of Los Angeles and Long Beach complex"
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
                            <p className="absolute bottom-5 left-5 font-heading text-sm font-bold uppercase tracking-[0.2em] text-white">
                                LA / LB Port Complex
                            </p>
                        </div>
                    </Reveal>
                    {VOLUME_STATS.slice(0, 4).map((stat, i) => (
                        <Reveal key={stat.label} delay={i * 0.07}>
                            <div className="flex h-full min-h-[136px] flex-col justify-between bg-ink p-5 sm:p-6 lg:min-h-[200px]">
                                <p className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gold">
                                    {stat.value}
                                </p>
                                <p className="mt-3 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-gray-400">
                                    {stat.label}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                    <Reveal className="col-span-2">
                        <div className="group relative h-44 overflow-hidden sm:h-52 lg:h-[200px]">
                            <img
                                src={IMAGES.fleetTruck}
                                alt="Pacific Expressway Class 8 truck moving containers through Southern California"
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-ink/70 to-transparent" />
                            <div className="absolute inset-y-0 left-5 flex flex-col justify-center sm:left-6">
                                <p className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-gold">
                                    900+
                                </p>
                                <p className="mt-1 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-white">
                                    Containers / Week
                                </p>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal className="col-span-2">
                        <div className="flex h-full min-h-[120px] items-center gap-4 border-2 border-ink p-5 sm:p-6">
                            <Check size={28} className="shrink-0 text-gold" aria-hidden="true" />
                            <div>
                                <p className="font-heading text-lg sm:text-xl font-bold uppercase tracking-[0.08em] text-ink">
                                    Overweight Corridor Capable
                                </p>
                                <p className="mt-1 text-sm text-gray-600">
                                    Permitted overweight moves between the ports and our yards.
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function DriverStrip() {
    return (
        <section
            className="relative overflow-hidden bg-ink"
            data-testid="driver-strip"
        >
            <div className="absolute inset-0" aria-hidden="true">
                <img
                    src={IMAGES.semiHighway}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover opacity-35"
                />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/30" aria-hidden="true" />
            <div className="container-px relative py-16 sm:py-20 lg:py-24">
                <Reveal className="max-w-2xl">
                    <Eyebrow>Careers at Pacific</Eyebrow>
                    <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
                        Drive With Pacific.
                    </h2>
                    <p className="mt-5 text-base sm:text-lg leading-relaxed text-gray-300">
                        We&rsquo;re always interested in meeting professional drivers who want
                        steady container work backed by real equipment — 100+ trucks, 500+
                        chassis and a company that&rsquo;s been family-owned since 2002.
                    </p>
                    <Link
                        to="/drive"
                        data-testid="driver-strip-apply-button"
                        className="group mt-8 inline-flex items-center gap-2 bg-gold px-8 py-4 font-heading text-sm font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-200 hover:bg-gold-light"
                    >
                        Apply to Drive
                        <ArrowRight
                            size={16}
                            className="transition-transform duration-200 group-hover:translate-x-1"
                            aria-hidden="true"
                        />
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <>
            <SEO
                title="Pacific Expressway | Southern California Logistics & Port Drayage"
                description="Family-owned since 2002. High-volume Los Angeles and Long Beach port drayage, transloading, cross-docking, warehousing, overweight & OOG freight and dedicated transportation — 100+ trucks, 500+ chassis."
                path="/"
            />
            <Hero />
            <StatsBar />
            <WhyGrid />
            <Marquee />
            <SolutionsPreview />
            <ProblemSolution />
            <BuiltForVolume />
            <DriverStrip />
            <CTASection />
        </>
    );
}
